import {
  LEDGER_SOURCE_PLAN_CODES,
  labelizeLedgerKind,
  labelizeSourcePlan,
} from './ledger-labels';

/**
 * Bucket registry for the member "Earnings Breakdown" panel. The panel groups
 * a slot's lifetime credits into buckets — normally one per `source_plan`,
 * plus "sub-buckets" that peel a specific ledger `kind` out of its host plan
 * into a row of its own (e.g. the mentor rider on Monthly Direct Bonus).
 * Shared by the API aggregation, the member panel, and the admin row editor
 * so all three agree on keys, splits, and labels.
 */
export type EarningsBucketDef = {
  /** Stable row key stored in `earnings_breakdown_rows` (a plan code, or the sub-bucket's kind). */
  key: string;
  /** Host `source_plan` — a sub-bucket hides/disables together with this plan. */
  plan: string;
  /** Set only on sub-buckets: the ledger `kind` this bucket claims from the host plan. */
  kind?: string;
};

/**
 * Kind-level splits carved out of a host plan's bucket. Keep this list short
 * and deliberate — every entry becomes a row the admin can arrange, rename,
 * and force-show. Future splits (binary 5th-pair, stairstep rank-up, …) are
 * one entry each.
 *
 * Known collision, accepted: a free-form `source_plan` equal to a sub-bucket
 * kind (only reachable via admin-adjust free text) merges into the split row,
 * because {@link bucketKeyForEntry} claims sub-buckets by kind first.
 */
export const EARNINGS_SUB_BUCKETS: EarningsBucketDef[] = [
  {
    key: 'monthly_direct_mentor_bonus',
    plan: 'monthly_direct_bonus',
    kind: 'monthly_direct_mentor_bonus',
  },
  // The Mentor's Bonus rider on Binary pairing (sponsor-tree upline % of a
  // downline's binary cash). Peeled out of the "Binary" bucket into its own
  // row — its credits were previously summed into Binary, so that tile's total
  // drops by this amount (intended correction, not a regression).
  {
    key: 'binary_mentor_bonus',
    plan: 'binary',
    kind: 'binary_mentor_bonus',
  },
];

/** Ledger `kind`s claimed by a sub-bucket — membership test for entry filters. */
export const EARNINGS_SUB_BUCKET_KINDS: ReadonlySet<string> = new Set(
  EARNINGS_SUB_BUCKETS.map((b) => b.kind!),
);

/**
 * Full bucket registry in default display order: every known ledger
 * source-plan code except `payout` (the breakdown is earnings-only — editor
 * parity with the legacy order editor), with each sub-bucket spliced
 * immediately after its host plan.
 */
export const EARNINGS_BUCKETS: EarningsBucketDef[] = LEDGER_SOURCE_PLAN_CODES.filter(
  (code) => code !== 'payout',
).flatMap((code) => [
  { key: code, plan: code },
  ...EARNINGS_SUB_BUCKETS.filter((b) => b.plan === code),
]);

const SUB_BUCKET_KEY_BY_KIND: Record<string, string> = Object.fromEntries(
  EARNINGS_SUB_BUCKETS.map((b) => [b.kind!, b.key]),
);

/**
 * Bucket key for one ledger credit: a sub-bucket kind maps to its own key,
 * everything else groups under its `source_plan` (unknown/free-form plans
 * included — they still bucket, they're just never in the registry).
 */
export function bucketKeyForEntry(sourcePlan: string, kind: string): string {
  return SUB_BUCKET_KEY_BY_KIND[kind] ?? sourcePlan;
}

/**
 * Display label for a breakdown row key. Sub-bucket keys resolve through the
 * ledger KIND layer (`features.ledger_labels`), plan keys through the
 * SOURCE-PLAN layer (`features.ledger_source_plan_labels`) — the same maps the
 * passbook, notifications, and admin views read, so renaming a row renames it
 * everywhere. There is deliberately no third label store.
 */
export function earningsBreakdownRowLabel(
  key: string,
  planLabels?: Record<string, string> | null,
  kindLabels?: Record<string, string> | null,
): string {
  const sub = EARNINGS_SUB_BUCKETS.find((b) => b.key === key);
  if (sub?.kind) return labelizeLedgerKind(sub.kind, kindLabels);
  return labelizeSourcePlan(key, planLabels);
}

/** Visibility mode for one configured breakdown row. */
export type EarningsBreakdownRowMode = 'always' | 'when_earned' | 'hidden';

/**
 * One entry of `tenant_config.features.earnings_breakdown_rows`. Array order
 * is display order.
 *   `always`      — show the row even at zero earnings (fabricated cash tile)
 *   `when_earned` — show only when the bucket has credits (legacy behavior)
 *   `hidden`      — suppress an active plan's row (display-only)
 */
export type EarningsBreakdownRow = { key: string; mode: EarningsBreakdownRowMode };

/**
 * Effective row config for a tenant — used by BOTH the API route and the
 * admin editor so the two resolve identically. Precedence:
 *   1. `earnings_breakdown_rows` (non-empty) — deduped by key, first wins;
 *   2. legacy `earnings_breakdown_order` — synthesized as `when_earned` rows
 *      (deprecated key kept as a read fallback, so legacy tenants keep their
 *      arrangement with zero data rewrite);
 *   3. `[]` — nothing configured, amount-sorted default.
 * The default `earnings_breakdown_rows` is `[]`, so an empty array means
 * "unconfigured" and falls through to the legacy order.
 */
export function resolveEarningsBreakdownRows(f: {
  earnings_breakdown_rows?: EarningsBreakdownRow[] | null;
  earnings_breakdown_order?: string[] | null;
}): EarningsBreakdownRow[] {
  const rows = f.earnings_breakdown_rows;
  if (Array.isArray(rows) && rows.length > 0) {
    const seen = new Set<string>();
    const out: EarningsBreakdownRow[] = [];
    for (const row of rows) {
      if (seen.has(row.key)) continue;
      seen.add(row.key);
      out.push(row);
    }
    return out;
  }
  const order = f.earnings_breakdown_order;
  if (Array.isArray(order) && order.length > 0) {
    return [...new Set(order)].map((key): EarningsBreakdownRow => ({ key, mode: 'when_earned' }));
  }
  return [];
}
